import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { calculateFormula } from "../../../api/formulaEngineApi";
import { useI18nLanguage } from "../../../i18n/hooks/useI18nLanguage";

function normalizeDataType(dt) {
  return String(dt || "").toLowerCase();
}

/**
 * Manual formula input dialog — built from formula.parameters[].
 */
function FormulaInputDialog({
  formula,
  missingParameters,
  message,
  blockedCode,
  onClose,
  onSuccess,
}) {
  const { t } = useTranslation("common");
  const { language } = useI18nLanguage();
  const isHe = String(language || "").toLowerCase().startsWith("he");
  const params = Array.isArray(formula?.parameters) ? formula.parameters : [];
  const missingSet = useMemo(() => {
    if (!Array.isArray(missingParameters) || missingParameters.length === 0) {
      return null;
    }
    return new Set(missingParameters.map((p) => String(p)));
  }, [missingParameters]);

  const visibleParams = params;
  // Params listed in missingParameters but absent from catalog (e.g. confirmation flags).
  const extraMissing =
    missingSet == null
      ? []
      : [...missingSet].filter(
          (name) => !params.some((p) => String(p.name) === name),
        );

  const [values, setValues] = useState(() => {
    const init = {};
    for (const p of params) {
      const dt = normalizeDataType(p.dataType);
      if (dt === "boolean") init[p.name] = false;
      else init[p.name] = "";
    }
    for (const name of extraMissing) {
      if (String(name).toLowerCase().includes("confirm")) init[name] = false;
      else init[name] = "";
    }
    return init;
  });
  const [units, setUnits] = useState(() => {
    const init = {};
    for (const p of params) {
      if (p.canonicalUnit) init[p.name] = p.canonicalUnit;
    }
    return init;
  });
  const [submitting, setSubmitting] = useState(false);

  const title = isHe
    ? formula?.nameHe || formula?.nameEn || formula?.formulaId
    : formula?.nameEn || formula?.nameHe || formula?.formulaId;

  const setValue = (name, v) =>
    setValues((prev) => ({ ...prev, [name]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formula?.formulaId || submitting) return;

    const inputs = {};
    const textInputs = {};
    const inputUnits = {};

    const allParamDefs = [
      ...visibleParams,
      ...extraMissing.map((name) => ({
        name,
        dataType: String(name).toLowerCase().includes("confirm")
          ? "Boolean"
          : "Text",
        required: true,
        displayName: name,
      })),
    ];

    for (const p of allParamDefs) {
      const name = p.name;
      const dt = normalizeDataType(p.dataType);
      const raw = values[name];
      if (dt === "boolean") {
        textInputs[name] = raw ? "true" : "false";
        continue;
      }
      if (dt === "text") {
        const s = String(raw ?? "").trim();
        if (!s && p.required !== false) {
          toast.error(
            t("formulaEngine.requiredField", {
              defaultValue: "Please fill required fields.",
            }),
          );
          return;
        }
        if (s) textInputs[name] = s;
        continue;
      }
      // Decimal / Integer
      if (raw === "" || raw == null) {
        if (p.required !== false) {
          toast.error(
            t("formulaEngine.requiredField", {
              defaultValue: "Please fill required fields.",
            }),
          );
          return;
        }
        continue;
      }
      const num = Number(raw);
      if (!Number.isFinite(num)) {
        toast.error(
          t("formulaEngine.invalidNumber", {
            defaultValue: "Enter a valid number.",
          }),
        );
        return;
      }
      inputs[name] = num;
      const unit = units[name];
      const canonical = p.canonicalUnit;
      if (unit && canonical && unit !== canonical) {
        inputUnits[name] = unit;
      }
    }

    setSubmitting(true);
    try {
      const body = { formulaId: formula.formulaId, inputs };
      if (Object.keys(textInputs).length) body.textInputs = textInputs;
      if (Object.keys(inputUnits).length) body.inputUnits = inputUnits;
      const data = await calculateFormula(body);
      const payload = data?.payload || {};
      onSuccess?.(payload, formula);
      onClose?.();
    } catch (err) {
      toast.error(
        err?.message ||
          t("formulaEngine.calculateFailed", {
            defaultValue: "Calculation failed.",
          }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (p) => {
    const dt = normalizeDataType(p.dataType);
    const accepted = String(p.acceptedUnits || "")
      .split(",")
      .map((u) => u.trim())
      .filter(Boolean);

    if (dt === "boolean") {
      return (
        <label
          key={p.name}
          className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200"
        >
          <input
            type="checkbox"
            checked={!!values[p.name]}
            onChange={(e) => setValue(p.name, e.target.checked)}
          />
          <span>
            {p.displayName || p.name}
            {missingSet?.has(String(p.name)) ? (
              <span className="ml-1 text-[10px] font-semibold uppercase text-amber-600 dark:text-amber-400">
                {t("formulaEngine.missing", { defaultValue: "missing" })}
              </span>
            ) : null}
          </span>
        </label>
      );
    }

    if (dt === "text") {
      return (
        <label key={p.name} className="block text-sm">
          <span className="mb-1 block text-gray-700 dark:text-gray-300">
            {p.displayName || p.name}
            {missingSet?.has(String(p.name)) ? (
              <span className="ml-1 text-[10px] font-semibold uppercase text-amber-600 dark:text-amber-400">
                {t("formulaEngine.missing", { defaultValue: "missing" })}
              </span>
            ) : null}
          </span>
          <input
            type="text"
            value={values[p.name] ?? ""}
            onChange={(e) => setValue(p.name, e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
          />
        </label>
      );
    }

    return (
      <label key={p.name} className="block text-sm">
        <span className="mb-1 block text-gray-700 dark:text-gray-300">
          {p.displayName || p.name}
          {p.canonicalUnit ? ` (${p.canonicalUnit})` : ""}
          {missingSet?.has(String(p.name)) ? (
            <span className="ml-1 text-[10px] font-semibold uppercase text-amber-600 dark:text-amber-400">
              {t("formulaEngine.missing", { defaultValue: "missing" })}
            </span>
          ) : null}
        </span>
        <div className="flex gap-2">
          <input
            type="number"
            step="any"
            value={values[p.name] ?? ""}
            onChange={(e) => setValue(p.name, e.target.value)}
            className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
          />
          {accepted.length > 1 ? (
            <select
              value={units[p.name] || p.canonicalUnit || accepted[0]}
              onChange={(e) =>
                setUnits((prev) => ({ ...prev, [p.name]: e.target.value }))
              }
              className="rounded-lg border border-gray-300 bg-white px-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
            >
              {accepted.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          ) : null}
        </div>
      </label>
    );
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-4">
      <div
        className="w-full max-w-md rounded-2xl bg-white p-4 shadow-xl dark:bg-gray-800"
        role="dialog"
        aria-modal="true"
      >
        <div className="mb-3 flex items-start justify-between gap-2">
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {title}
            </h2>
            {message ? (
              <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                {message}
              </p>
            ) : null}
            {blockedCode ? (
              <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                {blockedCode}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full px-2 py-1 text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label={t("close", { defaultValue: "Close" })}
          >
            ✕
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          {visibleParams.map(renderField)}
          {extraMissing.map((name) =>
            renderField({
              name,
              displayName: name,
              dataType: String(name).toLowerCase().includes("confirm")
                ? "Boolean"
                : "Text",
              required: true,
            }),
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              {t("cancel", { defaultValue: "Cancel" })}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-full bg-green px-4 py-1.5 text-sm font-semibold text-gray-900 disabled:opacity-50 dark:bg-[#CBEA7B] dark:text-black"
            >
              {submitting
                ? t("loading", { defaultValue: "Loading…" })
                : t("formulaEngine.calculate", { defaultValue: "Calculate" })}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default FormulaInputDialog;
